import type { Data } from '@puckeditor/core'
import type { ResponsiveLayers } from '../responsive'
import { PUCK_DATA_VERSION, PUCK_DATA_VERSION_FALLBACK } from './constants'

/** Read the saved-data version, falling back for pre-versioning saves. */
export const readPuckDataVersion = (data: unknown): string => {
  if (data && typeof data === 'object' && 'version' in data) {
    const v = (data as { version?: unknown }).version
    if (typeof v === 'string' && v) return v
  }
  return PUCK_DATA_VERSION_FALLBACK
}

/** Stamp the current version onto a payload about to be saved. */
export const stampPuckDataVersion = <T extends object>(
  data: T,
): T & { version: string } => ({
  ...data,
  version: PUCK_DATA_VERSION,
})

/**
 * JSON.stringify with every non-ASCII character escaped (\uXXXX). The
 * step/page-builder persistence munges 4-byte utf8 (emoji) into '?'
 * (legacy latin1 tables); ASCII-only payloads survive and JSON.parse
 * restores the original characters. Use for every content save.
 */
export const asciiSafeJson = (data: object): string =>
  JSON.stringify(data).replace(
    /[\u0080-\uffff]/g,
    c => '\\u' + c.charCodeAt(0).toString(16).padStart(4, '0'),
  )

/**
 * Parse a step API response into Puck data (steps store the whole Puck
 * object — content + root + zones + responsive layer — as a JSON string in
 * `content`). Returns null for old-format (non-puck) steps.
 */
export const parseStepPuckContent = (raw: any): Data | null => {
  if (typeof raw?.content === 'string') {
    try {
      const parsed = JSON.parse(raw.content)
      // parsed carries content + root + zones + the responsive layer — return
      // it whole so per-breakpoint overrides survive the round-trip.
      if (Array.isArray(parsed.content)) return parsed
    } catch {}
  }
  if (Array.isArray(raw?.content)) {
    // Preserve any sibling fields (zones, responsive) alongside content/root.
    return {
      ...raw,
      content: raw.content,
      root: raw.root ?? { breakType: 'editorial' },
    }
  }
  return null
}

export type ParsedPageContent = {
  content: Data['content']
  props: Record<string, any>
  version: string
  /**
   * Per-breakpoint prop overrides, absent on pages that have never been edited
   * outside Default. Dropping this on load used to silently destroy the layer:
   * the next publish wrote the page back without it. `parseStepPuckContent` has
   * always preserved it — pages were the odd one out.
   */
  responsive?: ResponsiveLayers
}

/**
 * Parse a page's `content` JSON string. Supports the current
 * `{ version?, content, props }` shape and the legacy plain-array shape.
 */
export const parsePagePuckContent = (
  contentString: string,
): ParsedPageContent => {
  const parsed = JSON.parse(contentString)
  if (Array.isArray(parsed)) {
    return { content: parsed, props: {}, version: PUCK_DATA_VERSION_FALLBACK }
  }
  return {
    content: parsed.content,
    props: parsed.props ?? {},
    version: readPuckDataVersion(parsed),
    ...(parsed.responsive ? { responsive: parsed.responsive } : {}),
  }
}
